/**
 * AuditReport — renders a full strategic prospect analysis.
 *
 * Used by the Audit page (fresh result) and the prospect detail view.
 * Every section animates in; the opening messages are copy-to-clipboard.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Brain,
  Sparkles,
  Target,
  AlertTriangle,
  MessageSquare,
  CalendarClock,
  Copy,
  Check,
  Flame,
  Heart,
  Compass,
  Shield,
  Zap,
  MessageCircle,
  GitBranch,
  Eye,
  Ban,
} from 'lucide-react';
import type { AuditAnalysis } from '@/hooks/useProspect';
import { TEMPERATURE_META, CATEGORY_META, scoreColor } from '@/lib/prospectMeta';
import { ScoreRing } from '@/components/ScoreRing';

interface AuditReportProps {
  analysis: AuditAnalysis;
}

export function AuditReport({ analysis: a }: AuditReportProps) {
  const { t } = useTranslation();
  const temp = TEMPERATURE_META[a.temperature];
  const cat = CATEGORY_META[a.category];

  return (
    <div className="space-y-5">
      {/* ── Verdict header ─────────────────────────────────────────── */}
      <Section delay={0}>
        <div className="card-hi relative overflow-hidden rounded-[26px] p-7 sm:p-8">
          <div
            className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full opacity-40 blur-3xl"
            style={{ background: `radial-gradient(circle, ${temp.color}, transparent 70%)` }}
          />
          <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center">
            <div className="shrink-0">
              <ScoreRing score={a.score} caption={t('audit.affinity')} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <Badge color={temp.color}>
                  {temp.emoji} {t('prospect.tempBadge', { temp: t(temp.labelKey).toLowerCase() })}
                </Badge>
                <Badge color="#a855f7">
                  {cat.emoji} {t(cat.labelKey)}
                </Badge>
              </div>
              <h2 className="font-display text-[clamp(18px,2.6vw,24px)] font-800 leading-[1.25] tracking-[-0.025em]">
                {a.headline}
              </h2>
              {(a.identity.display_name || a.identity.handle) && (
                <p className="mt-1.5 font-mono text-[12px] text-ink/50">
                  {a.identity.display_name}
                  {a.identity.handle ? ` · ${a.identity.handle}` : ''}
                  {a.identity.platform ? ` · ${a.identity.platform}` : ''}
                </p>
              )}
            </div>
          </div>
        </div>
      </Section>

      {/* ── Score breakdown ────────────────────────────────────────── */}
      {a.score_breakdown.length > 0 && (
        <Section delay={0.06}>
          <Card kicker="Scoring" title={t("audit.report.whyScore")} icon={<Target size={16} />}>
            <div className="space-y-3.5">
              {a.score_breakdown.map((d, i) => (
                <ScoreBar key={d.label} dim={d} index={i} />
              ))}
            </div>
          </Card>
        </Section>
      )}

      {/* ── Profile summary ────────────────────────────────────────── */}
      <Section delay={0.12}>
        <Card kicker="Lecture du prospect" title={t("audit.report.whoIsThis")} icon={<Brain size={16} />}>
          <p className="text-[14px] leading-[1.65] text-ink/80">{a.profile_summary}</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <TagBlock title={t("audit.report.interests")} items={a.interests} tint="#3b82f6" />
            <TagBlock title={t('audit.report.painPoints')} items={a.pain_points} tint="#f59e0b" />
          </div>
        </Card>
      </Section>

      {/* ── Psychological profile ──────────────────────────────────── */}
      <PsychSection psych={a.psych_profile} legacy={a.psychographic} delay={0.15} />

      {/* ── Buying signals ─────────────────────────────────────────── */}
      {a.buying_signals.length > 0 && (
        <Section delay={0.18}>
          <Card
            kicker="Opportunité"
            title={t("audit.report.openSignals")}
            icon={<Flame size={16} />}
          >
            <ul className="space-y-2">
              {a.buying_signals.map((s, i) => (
                <li key={i} className="flex gap-2.5 text-[13.5px] leading-[1.55] text-ink/80">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-to-br from-pink to-violet" />
                  {s}
                </li>
              ))}
            </ul>
          </Card>
        </Section>
      )}

      {/* ── Objections ─────────────────────────────────────────────── */}
      {a.objections.length > 0 && (
        <Section delay={0.24}>
          <Card
            kicker="Anticipation"
            title={t("audit.report.objections")}
            icon={<AlertTriangle size={16} />}
          >
            <div className="space-y-3">
              {a.objections.map((o, i) => (
                <div key={i} className="rounded-2xl border border-ink/[0.07] p-4">
                  <p className="text-[13.5px] font-700 text-ink/85">« {o.objection} »</p>
                  <p className="mt-1.5 flex gap-1.5 text-[13px] leading-[1.55] text-ink/65">
                    <span className="font-700 text-pink">→</span>
                    {o.counter}
                  </p>
                </div>
              ))}
            </div>
          </Card>
        </Section>
      )}

      {/* ── Approach ───────────────────────────────────────────────── */}
      {a.approach && (
        <Section delay={0.3}>
          <div
            className="card-hi rounded-[24px] p-6 sm:p-7"
            style={{
              background: 'linear-gradient(135deg, rgba(168,85,247,0.07), rgba(236,72,153,0.05))',
            }}
          >
            <p className="kicker text-violet">
              <Sparkles size={12} /> Stratégie d'Inea
            </p>
            <h3 className="mb-2 mt-1.5 font-display text-[17px] font-800 tracking-[-0.02em]">
              Votre angle d'approche
            </h3>
            <p className="text-[14px] leading-[1.65] text-ink/80">{a.approach}</p>
          </div>
        </Section>
      )}

      {/* ── Opening messages ───────────────────────────────────────── */}
      {a.opening_messages.length > 0 && (
        <Section delay={0.36}>
          <Card
            kicker={t('audit.report.actionKicker')}
            title={t("audit.report.messages")}
            icon={<MessageSquare size={16} />}
          >
            <div className="space-y-3">
              {a.opening_messages.map((m, i) => (
                <MessageCard key={i} message={m} />
              ))}
            </div>
          </Card>
        </Section>
      )}

      {/* ── Follow-up plan ─────────────────────────────────────────── */}
      {a.follow_up_plan.length > 0 && (
        <Section delay={0.42}>
          <Card
            kicker="Suivi"
            title={t("audit.report.followupPlan")}
            icon={<CalendarClock size={16} />}
          >
            <div className="space-y-3">
              {a.follow_up_plan.map((s, i) => (
                <div key={i} className="flex gap-3.5">
                  <div className="flex flex-col items-center">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue to-violet font-display text-[12px] font-800 text-white">
                      {i + 1}
                    </span>
                    {i < a.follow_up_plan.length - 1 && (
                      <span className="my-1 w-px flex-1 bg-ink/[0.12]" />
                    )}
                  </div>
                  <div className="flex-1 pb-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-display text-[13px] font-800 text-ink">{s.timing}</span>
                      <span className="rounded-full bg-ink/[0.05] px-2 py-0.5 font-mono text-[10px] font-700 uppercase tracking-[0.1em] text-ink/55">
                        {s.channel}
                      </span>
                    </div>
                    <p className="mt-1 text-[13.5px] leading-[1.55] text-ink/75">{s.action}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </Section>
      )}
    </div>
  );
}

// ── building blocks ──────────────────────────────────────────────────────────

function Section({ children, delay }: { children: React.ReactNode; delay: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function Card({
  kicker,
  title,
  icon,
  children,
}: {
  kicker: string;
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="card p-6 sm:p-7">
      <div className="mb-4 flex items-center gap-2.5">
        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-ink/[0.05] text-ink/65">
          {icon}
        </span>
        <div>
          <p className="font-mono text-[10px] font-700 uppercase tracking-[0.16em] text-ink/45">
            {kicker}
          </p>
          <h3 className="font-display text-[16px] font-800 tracking-[-0.02em]">{title}</h3>
        </div>
      </div>
      {children}
    </section>
  );
}

function Badge({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11.5px] font-700"
      style={{ background: `${color}1a`, color }}
    >
      {children}
    </span>
  );
}

function ScoreBar({ dim, index }: { dim: { label: string; value: number; rationale: string }; index: number }) {
  const color = scoreColor(dim.value);
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between">
        <span className="text-[13px] font-700 text-ink/85">{dim.label}</span>
        <span className="font-display text-[13px] font-800" style={{ color }}>
          {dim.value}
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-ink/[0.06]">
        <motion.div
          className="h-full rounded-full"
          style={{ background: `linear-gradient(90deg, ${color}99, ${color})` }}
          initial={{ width: 0 }}
          animate={{ width: `${dim.value}%` }}
          transition={{ delay: 0.2 + index * 0.08, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
      <p className="mt-1 text-[12px] leading-[1.45] text-ink/55">{dim.rationale}</p>
    </div>
  );
}

function TagBlock({ title, items, tint }: { title: string; items: string[]; tint: string }) {
  if (items.length === 0) return null;
  return (
    <div>
      <p className="mb-1.5 font-mono text-[10px] font-700 uppercase tracking-[0.14em] text-ink/45">
        {title}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, i) => (
          <span
            key={i}
            className="rounded-lg px-2 py-1 text-[12px] font-600"
            style={{ background: `${tint}14`, color: tint }}
          >
            {it}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Psychological-profile card. Renders the full archetype + drivers + values +
 * fears + comm/decision style + awareness stage + triggers + taboo grid.
 * Falls back gracefully when the model only returned the legacy free-text.
 */
function PsychSection({
  psych,
  legacy,
  delay,
}: {
  psych: import('@/hooks/useProspect').PsychProfile | undefined;
  legacy: string;
  delay: number;
}) {
  const { t } = useTranslation();
  const p = psych;
  // If we have neither the structured profile nor legacy text, hide the section.
  const hasStructured =
    !!p &&
    (p.archetype ||
      (Array.isArray(p.drivers) && p.drivers.length) ||
      (Array.isArray(p.values) && p.values.length) ||
      (Array.isArray(p.triggers) && p.triggers.length));
  if (!hasStructured && !legacy) return null;

  // Older audits (no psych_profile in DB) → render the legacy narrative
  // in the new shell so the UI stays consistent.
  if (!hasStructured) {
    return (
      <Section delay={delay}>
        <Card kicker={t('audit.report.psychProfile')} title={t('audit.report.howWorks')} icon={<Brain size={16} />}>
          <p className="text-[14px] leading-[1.65] text-ink/80">{legacy}</p>
        </Card>
      </Section>
    );
  }

  return (
    <Section delay={delay}>
      <Card kicker={t('audit.report.psychProfile')} title={t('audit.report.howWorks')} icon={<Brain size={16} />}>
        {/* Hero: archetype + 1-liner summary */}
        <div className="-mx-1 mb-5 flex flex-col gap-3 rounded-2xl bg-gradient-to-br from-violet-500/[0.08] via-pink-500/[0.05] to-blue-500/[0.06] p-4 sm:flex-row sm:items-center sm:gap-4">
          {p!.archetype && (
            <div className="shrink-0">
              <p className="font-mono text-[10px] font-700 uppercase tracking-[0.16em] text-violet">
                {t('audit.report.archetype')}
              </p>
              <p className="font-display text-[18px] font-800 leading-[1.15] tracking-[-0.02em] text-ink">
                {p!.archetype}
              </p>
            </div>
          )}
          {p!.summary && (
            <p className="text-[13.5px] leading-[1.6] text-ink/80 sm:flex-1 sm:border-l sm:border-ink/[0.08] sm:pl-4">
              {p!.summary}
            </p>
          )}
        </div>

        {/* Style chips: communication + decision + awareness */}
        {(p!.communication_style || p!.decision_style || p!.awareness_stage) && (
          <div className="mb-5 grid gap-2.5 sm:grid-cols-3">
            <StyleChip
              icon={<MessageCircle size={13} />}
              label={t('audit.report.commStyle')}
              value={p!.communication_style}
              tint="#3b82f6"
            />
            <StyleChip
              icon={<GitBranch size={13} />}
              label={t('audit.report.decisionStyle')}
              value={p!.decision_style}
              tint="#a855f7"
            />
            <StyleChip
              icon={<Eye size={13} />}
              label={t('audit.report.awarenessStage')}
              value={p!.awareness_stage}
              tint="#ec4899"
            />
          </div>
        )}

        {/* Drivers + Values grid */}
        <div className="grid gap-4 sm:grid-cols-2">
          <PsychList
            icon={<Compass size={13} />}
            title={t('audit.report.drivers')}
            items={p!.drivers}
            tint="#10b981"
          />
          <PsychList
            icon={<Shield size={13} />}
            title={t('audit.report.values')}
            items={p!.values}
            tint="#3b82f6"
          />
          <PsychList
            icon={<AlertTriangle size={13} />}
            title={t('audit.report.fears')}
            items={p!.fears}
            tint="#f59e0b"
          />
          <PsychList
            icon={<Zap size={13} />}
            title={t('audit.report.triggers')}
            items={p!.triggers}
            tint="#ec4899"
          />
        </div>

        {/* Taboo row — what to NEVER do */}
        {Array.isArray(p!.taboo) && p!.taboo.length > 0 && (
          <div className="mt-5 rounded-2xl border border-red-200/60 bg-red-50/40 p-4">
            <div className="mb-2 flex items-center gap-1.5">
              <Ban size={13} className="text-red-600" />
              <p className="font-mono text-[10px] font-700 uppercase tracking-[0.14em] text-red-700">
                {t('audit.report.avoid')}
              </p>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {p!.taboo.map((item, i) => (
                <span
                  key={i}
                  className="rounded-lg bg-white/80 px-2 py-1 text-[12px] font-600 text-red-800/90"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}
      </Card>
    </Section>
  );
}

function StyleChip({
  icon,
  label,
  value,
  tint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tint: string;
}) {
  if (!value) return null;
  return (
    <div className="rounded-xl border border-ink/[0.08] bg-white/60 p-3">
      <div
        className="mb-1 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-mono text-[9.5px] font-700 uppercase tracking-[0.12em]"
        style={{ background: `${tint}14`, color: tint }}
      >
        {icon} {label}
      </div>
      <p className="font-display text-[14px] font-800 leading-[1.2] tracking-[-0.01em] text-ink">
        {value}
      </p>
    </div>
  );
}

function PsychList({
  icon,
  title,
  items,
  tint,
}: {
  icon: React.ReactNode;
  title: string;
  items: string[];
  tint: string;
}) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div>
      <div className="mb-2 flex items-center gap-1.5">
        <span style={{ color: tint }}>{icon}</span>
        <p className="font-mono text-[10px] font-700 uppercase tracking-[0.14em] text-ink/55">
          {title}
        </p>
      </div>
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li
            key={i}
            className="flex items-start gap-2 text-[13px] leading-[1.45] text-ink/80"
          >
            <span
              className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ background: tint }}
            />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MessageCard({ message }: { message: { angle: string; text: string } }) {
  const [copied, setCopied] = useState(false);

  function copy(): void {
    void navigator.clipboard.writeText(message.text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }

  return (
    <div className="group rounded-2xl border border-ink/[0.08] bg-white/60 p-4 transition hover:border-pink/30">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="flex items-center gap-1.5 font-mono text-[10px] font-700 uppercase tracking-[0.12em] text-pink">
          <Heart size={11} /> {message.angle}
        </span>
        <button
          onClick={copy}
          className="inline-flex items-center gap-1 rounded-lg bg-ink/[0.05] px-2 py-1 text-[11px] font-700 text-ink/60 transition hover:bg-pink/10 hover:text-pink"
        >
          {copied ? <Check size={11} /> : <Copy size={11} />}
          {copied ? 'Copié' : 'Copier'}
        </button>
      </div>
      <p className="text-[13.5px] leading-[1.6] text-ink/85">{message.text}</p>
    </div>
  );
}
